import { apiFetchRaw } from '@/auth/api';
import type { EhNotification } from '@/features/employeeportal/employeeportal.api';

/**
 * The app's one inbox (`eh_notifications`), read by the shell's bell and the
 * /notifications page. The API is the Employee Hub's (it was born there), but
 * it serves every module: custom-module automations write `custom_module`
 * notices, the Employee Hub writes `order`, `event` and `rsvp`. Every call is
 * the signed-in user's own inbox; the server pins `user_id` itself.
 */
export type AppNotification = EhNotification & { related_id?: string | null };

export interface NotificationPage {
  ok: boolean;
  data: AppNotification[];
  /** Unread across the whole inbox (capped at 100 by the server). */
  unreadCount: number;
  hasMore?: boolean;
}

const BASE = '/employeeportal/notifications';

export const NOTIFICATIONS_KEY = ['notifications'] as const;
export const UNREAD_KEY = ['notifications', 'unread'] as const;
export const listKey = (limit: number) => ['notifications', 'list', limit] as const;

export function listNotifications(
  opts: { limit?: number; before?: string | null; unread?: boolean } = {},
  signal?: AbortSignal,
): Promise<NotificationPage> {
  const q = new URLSearchParams();
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.before) q.set('before', opts.before);
  if (opts.unread) q.set('unread', '1');
  const qs = q.toString();
  return apiFetchRaw<NotificationPage>(`${BASE}${qs ? `?${qs}` : ''}`, { method: 'GET', signal });
}

export async function unreadCount(signal?: AbortSignal): Promise<number> {
  const r = await apiFetchRaw<{ ok: boolean; unread: number }>(`${BASE}/unread-count`, { method: 'GET', signal });
  return typeof r?.unread === 'number' ? r.unread : 0;
}

export function markNotificationRead(id: string): Promise<unknown> {
  return apiFetchRaw(`${BASE}/${encodeURIComponent(id)}/read`, { method: 'POST' }, { mutation: true });
}

export function markAllNotificationsRead(): Promise<unknown> {
  return apiFetchRaw(`${BASE}/read-all`, { method: 'POST' }, { mutation: true });
}
